import React, { useEffect, useRef, useState } from "react";
import { MessageCircle } from "lucide-react";

interface ChatIconProps {
  onClick: () => void;
  isConnected: boolean;
  style?: React.CSSProperties;
}

const PADDING = 16; // 1rem

export const ChatIcon: React.FC<ChatIconProps> = ({
  onClick,
  isConnected,
  style = {},
}) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const size = 56;
  const [coords, setCoords] = useState({ left: 20, top: 20 });

  // store drag offset
  const dragOffset = useRef({ x: 0, y: 0 });
  const dragged = useRef(false);
  const dragging = useRef(false);

  // Convert bottom/right → left/top
  useEffect(() => {
    let left = PADDING;
    let top = PADDING;

    if (style.left !== undefined)
      left = parseInt(String(style.left), 10) + PADDING;
    else if (style.right !== undefined)
      left =
        window.innerWidth - size - parseInt(String(style.right), 10) - PADDING;

    if (style.top !== undefined)
      top = parseInt(String(style.top), 10) + PADDING;
    else if (style.bottom !== undefined)
      top =
        window.innerHeight -
        size -
        parseInt(String(style.bottom), 10) -
        PADDING;

    setCoords({ left, top });
  }, [style]);

  // ULTRA-SMOOTH DRAG: Pointer Capture after movement
  const onPointerDown = (e: React.PointerEvent) => {
    dragged.current = false;
    dragging.current = true;

    dragOffset.current = {
      x: e.clientX - coords.left,
      y: e.clientY - coords.top,
    };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging.current) return;

    if (!dragged.current) {
      // movement threshold to avoid accidental capture
      const dx = e.clientX - (coords.left + dragOffset.current.x);
      const dy = e.clientY - (coords.top + dragOffset.current.y);
      if (Math.abs(dx) < 2 && Math.abs(dy) < 2) return;

      // activate pointer capture only WHEN drag actually starts
      wrapperRef.current?.setPointerCapture(e.pointerId);
      dragged.current = true;
    }

    const newLeft = Math.max(
      PADDING,
      Math.min(
        e.clientX - dragOffset.current.x,
        window.innerWidth - size - PADDING
      )
    );

    const newTop = Math.max(
      PADDING,
      Math.min(
        e.clientY - dragOffset.current.y,
        window.innerHeight - size - PADDING
      )
    );

    setCoords({ left: newLeft, top: newTop });
  };

  const onPointerUp = (e: React.PointerEvent) => {
    if (dragged.current) {
      wrapperRef.current?.releasePointerCapture(e.pointerId);
    }
    dragging.current = false;
  };

  const handleClick = () => {
    if (dragged.current) return; // don’t trigger click after drag
    onClick();
  };

  return (
    <div
      ref={wrapperRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      className="fixed"
      style={{
        position: "fixed",
        left: coords.left,
        top: coords.top,
        width: size,
        height: size,
        zIndex: style.zIndex || 50,
        cursor: dragged.current ? "grabbing" : "grab",
      }}
    >
      <button
        onClick={handleClick}
        className="absolute inset-0 flex items-center justify-center bg-orange-500 hover:bg-orange-600 rounded-full text-white shadow-lg transition-all"
      >
        <MessageCircle className="w-6 h-6" />

        <div
          className={`absolute -top-1 -right-1 w-4 h-4 rounded-full border-2 border-white ${
            isConnected ? "bg-green-500" : "bg-red-500"
          }`}
        />

        {!isConnected && (
          <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-ping" />
        )}
      </button>
    </div>
  );
};
